import type {RefundExpandedCommon} from '../../../api/payment';
import type {
  Handle3dSecureFn,
  ReturnUrl,
} from '../../../utils/entity/payment/handleRequiresActionPayment';

import {cardStore, paymentStore, refundStore} from '../../../api/payment';
import {handleRequiresActionPayment} from '../../../utils/entity/payment';
import {AppError} from '../../../utils/errors/app';
import {logger} from '../../../utils/logger';

export const retryRefund = async (
  refund: RefundExpandedCommon,
  {
    handle3dSecure,
    returnUrl,
  }: {
    handle3dSecure: Handle3dSecureFn;
    returnUrl: ReturnUrl;
  },
) => {
  const isStripeRefund = !refund.refund_payment_id;

  if (isStripeRefund) {
    await retryRefundByStripe(refund);
  } else {
    /**
     * That means that a coach paid us by card successfully
     * but as we here and retrying to refund, it means that we failed to refund
     * ie. send that money back to a client
     */
    const isRefundPaymentSucceeded =
      refund.refundPayment?.status === 'complete' ||
      refund.refundPayment?.status === 'paid';

    /**
     * If a refund by a card is succeeded,
     * we can face a situation where the refund itself can be failed.
     *
     * In this case we need to retry the refund via Stripe.
     * Maybe there is some logic on the backend side that handle this situation.
     * But I'm not sure. Need to ask the backend team.
     * This logic has moved from the mobile app as is and it seems to work
     */
    if (isRefundPaymentSucceeded) {
      await retryRefundByStripe(refund);
    } else {
      await retryRefundByCard(refund, {
        handle3dSecure,
        returnUrl,
      });
    }
  }
};

const retryRefundByStripe = async (refund: RefundExpandedCommon) => {
  try {
    await refundStore.execute(refund);
  } catch (error) {
    logger.error(error);

    throw error;
  }
};

const retryRefundByCard = async (
  refund: RefundExpandedCommon,
  {
    handle3dSecure,
    returnUrl,
  }: {
    handle3dSecure: Handle3dSecureFn;
    returnUrl: ReturnUrl;
  },
) => {
  try {
    if (!refund.refundPayment) {
      throw new AppError('Refund payment not found!');
    }

    const card = await cardStore.fetch({
      _id: refund.refundPayment.card_id!,
    });

    const result = await paymentStore.execute(refund.refundPayment);

    await handleRequiresActionPayment({
      card: card,
      clientSecret: result.clientSecret,
      handle3dSecure,
      payment: result.payment,
      returnUrl,
    });
  } catch (error) {
    logger.error(error);

    throw error;
  }
};
